/**
 * Media Intelligence Chat Service
 * Handles AI-powered chat for Media Intelligence Dashboard
 * Converted from JS to TypeScript
 */

import { logError } from './logger';
import { fetchEffectiveAgentProfile } from './agentProfileService';
import { getAgentProfile, buildSectionsPrompt, PERFORMANCE_ADVISOR, MMM_ADVISOR, INSIGHT_ADVISOR, sectionsToSectionInfo } from '../config/agentProfiles';
import { matchChartToQuestion } from './chatChartMatcher';
import { composedTips } from '../config/composedTips';

// The three modules lifted out of this file. Re-exported so that not one call
// site in the app moves — and so the `vi.mock('@/services/mediaChatService')`
// factories in the chat suites keep intercepting, since a path-based mock only
// fires on the path the importer asks for.
//
// `export type *` for the types module (isolatedModules is on); `export *` for
// the other two, which export values.
export type * from './mediaChatTypes';
export * from './mediaChatContext';
export * from './mediaChatWelcome';

// And imported for this module's OWN use — a re-export hands names to callers,
// it does not put them in scope here.
import type {
  ChatContext, ChatLink, ChatMediaData, ChatMessage, ChatMode, FollowUpQuestion,
  GuidedQuestion, SectionInfo,
} from './mediaChatTypes';
import { extractPageContext, extractMMMContext } from './mediaChatContext';
import { generateWelcomeSuggestions } from './mediaChatWelcome';







// ============================================================
// GUIDED QUESTIONS (canonical source: agentProfiles.ts)
// ============================================================

export const GUIDED_QUESTIONS: GuidedQuestion[] = PERFORMANCE_ADVISOR.guidedQuestions;



// ============================================================
// MMM GUIDED QUESTIONS (canonical source: agentProfiles.ts)
// ============================================================

export const MMM_GUIDED_QUESTIONS: GuidedQuestion[] = MMM_ADVISOR.guidedQuestions;

export const INSIGHT_GUIDED_QUESTIONS: GuidedQuestion[] = INSIGHT_ADVISOR.guidedQuestions;

// ============================================================
// CHAT-TO-REPORT TRIGGER
// ============================================================

/**
 * Thai + English keywords that signal the user wants a formatted report.
 * When detected, the response includes `reportTrigger: true` so the
 * component can call `onGenerateReport()` instead of (or in addition to)
 * the normal AI text response.
 */
const REPORT_INTENT_KEYWORDS = [
  'สร้างรายงาน',
  'รายงาน',
  'สรุปรายงาน',
  'export',
  'report',
  'generate report',
  'ออกรายงาน',
  'พิมพ์รายงาน',
  'ดาวน์โหลดรายงาน',
  'download report',
];

/**
 * Detects whether the user's message is asking for a report generation.
 * Only active when the agent profile has `showReportButton: true`.
 */
export const detectReportIntent = (
  userMessage: string,
  mode: ChatMode
): boolean => {
  // Built-in, not the override: this reads showReportButton, which is wiring
  // rather than authored text and therefore not editable — and the function is
  // synchronous, which an override read could not be.
  const profile = getAgentProfile(mode);
  if (!profile.showReportButton) return false;
  const lower = userMessage.toLowerCase().trim();
  return REPORT_INTENT_KEYWORDS.some((kw) => lower.includes(kw.toLowerCase()));
};

// ============================================================
// UNIFIED CHAT (routes through agentService — Hermes-ready)
// ============================================================

/**
 * sendChatMessage — the Hermes-ready chat entry point.
 *
 * Routes through `agentService.sendMessage()` which abstracts the backend:
 * - When VITE_AGENT_BACKEND=gemini (default): calls Gemini via server proxy
 * - When VITE_AGENT_BACKEND=hermes: calls Hermes agent runtime skill/port
 *
 * This replaces the old `generateChatResponse` which talked to Gemini directly.
 * The Hermes swap (Phase 2) becomes a pure env-var change — no component edits.
 *
 * Issue: YDMA-159
 */
export const sendChatMessage = async (
  userMessage: string,
  pageContext: ChatContext,
  conversationHistory: ChatMessage[] = [],
  mode: ChatMode = 'performance',
  /** Separates two pages that share a mode — see MediaChatBubble's `scope`. */
  scope?: string,
  /**
   * Whether this page can actually act on a section link. False sends no
   * section list at all, because the profile's sections belong to /campaigns:
   * the dashboard shares the 'performance' mode but renders no Metrics
   * Correlation and no Broker Performance, and a model handed that list names
   * them.
   */
  canNavigate = true,
): Promise<{
  text: string;
  links: ChatLink[];
  insight: string | null;
  followUpQuestions: FollowUpQuestion[];
  reportTrigger: boolean;
  chart?: import('./agentService').ChartSpec | null;
}> => {
  const profile = await fetchEffectiveAgentProfile(mode);

  // Check if user is requesting a report (same as old path)
  const reportTrigger = detectReportIntent(userMessage, mode);
  if (reportTrigger) {
    return {
      text: 'กำลังสร้างรายงาน HTML ให้คุณครับ! รอสักครู่...\n\nคลิกปุ่ม "รายงาน" ด้านล่างเพื่อเปิดรายงานแบบเต็มหน้าจอ หรือพิมพ์เป็น PDF ได้จากปุ่ม "พิมพ์" ในหน้ารายงาน',
      links: [],
      insight: null,
      followUpQuestions: profile.defaultFollowUps,
      reportTrigger: true,
    };
  }

  try {
    // Lazy-import agentService to avoid circular dependency at module load time
    const { default: agentService } = await import('./agentService');

    const response = await agentService.sendMessage({
      skill: mode,
      context: pageContext,
      message: userMessage,
      history: conversationHistory,
      systemPrompt: profile.roleStatement,
      // The editable tips, then the page's rules from code: composed after the
      // override, so a saved card can never drop or freeze them.
      analysisTips: composedTips(profile),
      sectionsPrompt: canNavigate ? buildSectionsPrompt(profile) : '',
      scope,
    });

    return {
      text: response.text,
      links: response.links,
      insight: response.insight,
      followUpQuestions:
        response.followUpQuestions.length > 0
          ? response.followUpQuestions
          : profile.defaultFollowUps,
      reportTrigger: false,
      chart: response.chart ?? matchChartToQuestion(userMessage, pageContext),
    };
  } catch (error) {
    logError('MediaChatService', 'sendChatMessage error', error);
    const message = error instanceof Error ? error.message : String(error);
    const isProxyMissing = /Failed to fetch/i.test(message);
    const isKeyMissing = /AI service not configured|GEMINI_API_KEY/i.test(message);
    const isProxy404 = /^Gemini proxy 404/i.test(message);
    const isTimeout = /timed out/i.test(message);

    let userText: string;
    const isParseError = error instanceof Error && error.name === 'AgentParseError';
    if (isParseError) {
      userText = "AI ตอบกลับมาไม่ถูก format — ลองถามใหม่อีกครั้งหรือเลือกคำถามแนะนำด้านบน";
    } else if (isTimeout) {
      userText = "AI ใช้เวลาตอบนานเกินไป — ลองถามคำถามสั้นกว่านี้ หรือเลือกคำถามแนะนำด้านบน";
    } else if (isProxyMissing || isProxy404) {
      userText = "AI service ไม่พร้อมใช้งาน — ลอง restart dev server (`pnpm dev`) แล้ว refresh หน้าใหม่ หรือลองใช้ guided questions ด้านบนก่อน";
    } else if (isKeyMissing) {
      userText = "AI service ยังไม่ได้ตั้งค่า — ต้องตั้ง GEMINI_API_KEY ใน .env (ไม่มี VITE_ prefix) แล้ว restart server";
    } else {
      userText = `เกิดข้อผิดพลาด: ${message.substring(0, 200)}\n\nลองอีกครั้งหรือเลือกคำถามแนะนำด้านบน`;
    }

    return {
      text: userText,
      links: [],
      insight: null,
      followUpQuestions: [
        { emoji: '📊', text: 'สรุปผลของฉันหน่อย' },
        { emoji: '📈', text: 'ช่องไหนดีสุด?' },
      ],
      reportTrigger: false,
    };
  }
};

// ============================================================
// STREAMING CHAT (incremental text rendering)
// ============================================================

/**
 * sendChatMessageStream — streaming variant of sendChatMessage.
 *
 * Calls `agentService`'s streaming path which uses `geminiClient.streamChat()`.
 * The `onTextChunk` callback receives incremental text as it arrives, allowing
 * the UI to render the response progressively instead of showing a spinner.
 *
 * Returns the full structured response when the stream completes (same shape
 * as sendChatMessage).
 *
 * Issue: YDMA-178 §2.2
 */
export const sendChatMessageStream = async (
  userMessage: string,
  pageContext: ChatContext,
  conversationHistory: ChatMessage[] = [],
  mode: ChatMode = 'performance',
  onTextChunk: (partialText: string) => void,
  /** Separates two pages that share a mode — see MediaChatBubble's `scope`. */
  scope?: string,
  /** See sendChatMessageStream. */
  canNavigate = true,
): Promise<{
  text: string;
  links: ChatLink[];
  insight: string | null;
  followUpQuestions: FollowUpQuestion[];
  reportTrigger: boolean;
  chart?: import('./agentService').ChartSpec | null;
}> => {
  const profile = await fetchEffectiveAgentProfile(mode);

  const reportTrigger = detectReportIntent(userMessage, mode);
  if (reportTrigger) {
    return {
      text: 'กำลังสร้างรายงาน HTML ให้คุณครับ! รอสักครู่...\n\nคลิกปุ่ม "รายงาน" ด้านล่างเพื่อเปิดรายงานแบบเต็มหน้าจอ หรือพิมพ์เป็น PDF ได้จากปุ่ม "พิมพ์" ในหน้ารายงาน',
      links: [],
      insight: null,
      followUpQuestions: profile.defaultFollowUps,
      reportTrigger: true,
    };
  }

  try {
    const { default: agentService } = await import('./agentService');

    const response = await agentService.streamSendMessage(
      {
        skill: mode,
        context: pageContext,
        message: userMessage,
        history: conversationHistory,
        systemPrompt: profile.roleStatement,
        analysisTips: composedTips(profile),
        sectionsPrompt: canNavigate ? buildSectionsPrompt(profile) : '',
        scope,
      },
      onTextChunk
    );

    return {
      text: response.text,
      links: response.links,
      insight: response.insight,
      followUpQuestions:
        response.followUpQuestions.length > 0
          ? response.followUpQuestions
          : profile.defaultFollowUps,
      reportTrigger: false,
      chart: response.chart ?? matchChartToQuestion(userMessage, pageContext),
    };
  } catch (error) {
    logError('MediaChatService', 'sendChatMessageStream error', error);

    // Fall back to non-streaming
    return sendChatMessage(userMessage, pageContext, conversationHistory, mode, scope, canNavigate);
  }
};
// ============================================================

/**
 * Generates intelligent follow-up questions based on dashboard data
 * @param data - Media intelligence data
 * @param _lastQuestion - The user's last question (reserved for future use)
 * @returns Array of follow-up question objects
 */
export const generateContextualFollowUps = (
  data: ChatMediaData,
  _lastQuestion: string = ''
): FollowUpQuestion[] => {
  const { metrics, channels, mmmRecommendations } = data;
  const followUps: FollowUpQuestion[] = [];

  // Channel-specific follow-ups
  if (channels && channels.length > 0) {
    // Only channels that reported revenue can be best or worst on ROAS.
    const withRoas = channels.filter((c) => typeof c.roas === 'number' && c.roas > 0);
    const bestChannel = [...withRoas].sort((a, b) => (b.roas || 0) - (a.roas || 0))[0];
    const worstChannel = [...withRoas].sort((a, b) => (a.roas || 0) - (b.roas || 0))[0];

    if (bestChannel && worstChannel && bestChannel.id !== worstChannel.id) {
      followUps.push({
        emoji: '📈',
        text: `ทำไม ${bestChannel.name} ถึงทำได้ดีกว่า ${worstChannel.name}?`,
      });
      followUps.push({
        emoji: '💡',
        text: `จะเสริช่อง ${worstChannel.name} ยังไงดี?`,
      });
    }

    // Budget optimization follow-up
    if (mmmRecommendations) {
      followUps.push({
        emoji: '💰',
        text: 'MMM แนะนำจัดงบยังไง?',
      });
    }
  }

  // Conversion funnel follow-ups
  if (metrics?.conversionRate) {
    if (metrics.conversionRate < 60) {
      followUps.push({
        emoji: '📉',
        text: 'ทำไม conversion ของฉันถึงต่ำกว่า 60%?',
      });
    } else {
      followUps.push({
        emoji: '✅',
        text: 'อะไรทำให้ conversion ของฉันแรง?',
      });
    }
  }

  // Cost efficiency follow-ups
  if (metrics?.cpl) {
    followUps.push({
      emoji: '💸',
      text:
        metrics.cpl > 120 ? 'จะลดต้นทุนต่อ conversion ยังไงดี?' : 'งบที่ลงไปคุ้มไหม?',
    });
  }

  // Add generic follow-ups if we don't have enough
  if (followUps.length < 3) {
    followUps.push(
      { emoji: '📊', text: 'โชว์ trend 4 สัปดาห์หน่อย' },
      { emoji: '🎯', text: 'แคมเปญไหนต้องดูด่วน?' },
      { emoji: '🔄', text: 'ถ้าย้ายงบประมาณจะเป็นไง?' }
    );
  }

  // Return up to 4 relevant follow-ups
  return followUps.slice(0, 4);
};


// ============================================================
// SECTION NAVIGATION HELPERS
// ============================================================

/**
 * Gets available sections for navigation (config-driven via agent profiles).
 * Defaults to the Performance Advisor profile.
 */
export const getAvailableSections = (): SectionInfo[] =>
  sectionsToSectionInfo(PERFORMANCE_ADVISOR);

export default {
  GUIDED_QUESTIONS,
  extractPageContext,
  extractMMMContext,
  detectReportIntent,
  getAvailableSections,
  generateContextualFollowUps,
  generateWelcomeSuggestions,
  sendChatMessage,
  sendChatMessageStream,
};
